import type { Locale } from "./locales.ts";

/**
 * The name a reader sees, in the language they're reading in.
 *
 * Three readers apply this rule today: the till (`apps/pos/src/lib/menu.ts`,
 * re-exporting from here so its search and its display cannot drift apart),
 * the back office console, and — as a SQL coalesce rather than this function,
 * because the client there is a phone on 3G with no catalogue in memory —
 * the diner app (`apps/api/src/routes/diner.ts`).
 *
 * The locale is always the *reader's* locale, never the store's base
 * language: a manager working the back office in Khmer sees Khmer even on a
 * store whose first language is English, and vice versa.
 */

/** Anything with a name that may have been translated. Items, categories, modifiers and tags all qualify. */
export type Named = { name: string; i18n?: Partial<Record<Locale, { name?: string }>> };

/**
 * The name to show, and therefore the name to search.
 *
 * The serializer sets `name` only when it is non-empty (`store/items.ts`), so
 * `??` is enough: a record with no translation for this locale falls back to
 * the base name, which is what the row shows anyway.
 */
export function displayName(item: Named, locale: Locale): string {
  return item.i18n?.[locale]?.name ?? item.name;
}
